export type DatePreset = 'all' | 'today' | 'yesterday' | 'last7' | 'last30' | 'custom';

function getTimeZoneCalendarDate(timeZone: string, baseDate = new Date()): Date {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(baseDate);

  const year = Number(parts.find((part) => part.type === 'year')?.value ?? '1970');
  const month = Number(parts.find((part) => part.type === 'month')?.value ?? '1');
  const day = Number(parts.find((part) => part.type === 'day')?.value ?? '1');

  return new Date(Date.UTC(year, month - 1, day));
}

function formatDateParam(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function buildPresetRange(
  preset: Exclude<DatePreset, 'custom'>,
  timeZone: string,
  baseDate = new Date(),
) {
  if (preset === 'all') {
    return { start: undefined, end: undefined };
  }

  const endDate = getTimeZoneCalendarDate(timeZone, baseDate);
  const startDate = new Date(endDate);

  if (preset === 'yesterday') {
    startDate.setUTCDate(startDate.getUTCDate() - 1);
    endDate.setUTCDate(endDate.getUTCDate() - 1);
  } else if (preset === 'last7') {
    startDate.setUTCDate(startDate.getUTCDate() - 6);
  } else if (preset === 'last30') {
    startDate.setUTCDate(startDate.getUTCDate() - 29);
  }

  return {
    start: formatDateParam(startDate),
    end: formatDateParam(endDate),
  };
}

function inferDatePreset(
  startDate: string,
  endDate: string,
  timeZone: string,
): DatePreset {
  if (!startDate && !endDate) {
    return 'all';
  }

  for (const preset of ['today', 'yesterday', 'last7', 'last30'] as const) {
    const range = buildPresetRange(preset, timeZone);
    if (range.start === startDate && range.end === endDate) {
      return preset;
    }
  }

  return 'custom';
}

export function getStoredDatePreset(
  searchParams: URLSearchParams,
  startDate: string,
  endDate: string,
  timeZone: string,
): DatePreset {
  const preset = searchParams.get('date_preset');
  if (preset === 'all' || preset === 'today' || preset === 'yesterday' || preset === 'last7' || preset === 'last30' || preset === 'custom') {
    return preset;
  }
  return inferDatePreset(startDate, endDate, timeZone);
}

export function buildDefaultCallHistorySearchParams(
  searchParams: URLSearchParams,
  timeZone: string,
  baseDate = new Date(),
): URLSearchParams | null {
  const hasExplicitDateSelection = ['date_preset', 'start_date', 'end_date']
    .some((key) => searchParams.has(key));
  if (hasExplicitDateSelection) {
    return null;
  }

  const range = buildPresetRange('today', timeZone, baseDate);
  const nextParams = new URLSearchParams(searchParams);
  nextParams.set('date_preset', 'today');
  nextParams.set('start_date', range.start!);
  nextParams.set('end_date', range.end!);
  nextParams.set('page', '1');
  return nextParams;
}
